import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

const COIN_OPTIONS = [
  {
    symbol: 'USDT',
    name: 'Tether USD',
    icon: (
      <span className="p2p-fam-coin-svg">
        <svg width="20" height="20" viewBox="0 0 32 32">
          <circle cx="16" cy="16" r="16" fill="#26A17B" />
          <path
            fill="#FFF"
            d="M17.922 17.383c-.05.003-.684.037-1.922.037-1.07 0-1.748-.033-1.916-.037v-1.688c1.396.06 3.125.048 3.838 0v1.688zm-.006-2.584c-.05.003-.68.037-1.916.037-1.07 0-1.754-.033-1.922-.037v-.55c.577.027 1.25.042 1.922.042.673 0 1.346-.015 1.916-.042v.55zm4.84 5.347c-.282.802-1.34 1.488-2.88 1.874v3.98h-2.128v-3.816c-.57.042-1.18.064-1.815.064-.617 0-1.21-.02-1.764-.06v3.812h-2.128v-3.98c-1.54-.386-2.598-1.072-2.88-1.874-.05-.143-.075-.29-.075-.438 0-.96 1.05-1.765 2.65-2.072l.305-.052v-1.264c-1.84-.275-3.155-.99-3.155-1.848 0-.857 1.315-1.572 3.155-1.847V7.12h7.844v3.473c1.84.275 3.154.99 3.154 1.847 0 .858-1.314 1.573-3.154 1.848v1.264l.305.052c1.6.307 2.65 1.112 2.65 2.072 0 .148-.025.295-.075.438z"
          />
        </svg>
      </span>
    )
  },
  {
    symbol: 'BTC',
    name: 'Bitcoin',
    icon: (
      <span className="p2p-fam-coin-svg">
        <svg width="20" height="20" viewBox="0 0 32 32">
          <circle cx="16" cy="16" r="16" fill="#F7931A" />
          <path
            fill="#FFF"
            d="M22.5 13.5c.3-2-1.2-3.1-3.3-3.8l.7-2.7-1.6-.4-.7 2.6c-.4-.1-.8-.2-1.3-.3l.7-2.7-1.6-.4-.7 2.7c-.3-.1-.7-.2-1-.3l-2.2-.6-.4 1.7s1.2.3 1.2.3c.7.2.8.7.8 1.1l-.8 3.2c0 0 .1 0 .2.1l-.2-.1-1.1 4.5c-.1.2-.3.6-.8.4 0 0-1.2-.3-1.2-.3l-.8 1.8 2.1.5c.4.1.8.2 1.1.3l-.7 2.8 1.6.4.7-2.7c.4.1.9.2 1.3.3l-.7 2.7 1.6.4.7-2.8c2.8.5 4.9.3 5.8-2.2.7-2 0-3.2-1.5-3.9 1.1-.3 1.9-1 2.1-2.5zm-3.8 5.3c-.5 2-3.9.9-5 .7l.9-3.6c1.1.3 4.6.8 4.1 2.9zm.5-5.4c-.5 1.8-3.3.9-4.2.7l.8-3.3c.9.2 3.8.7 3.4 2.6z"
          />
        </svg>
      </span>
    )
  },
  {
    symbol: 'ETH',
    name: 'Ethereum',
    icon: (
      <span className="p2p-fam-coin-svg">
        <svg width="20" height="20" viewBox="0 0 32 32">
          <circle cx="16" cy="16" r="16" fill="#627EEA" />
          <path fill="#FFF" d="M16 4l-7 12 7 4 7-4-7-12zm0 17.5l-7-4 7 10.5 7-10.5-7 4z" />
        </svg>
      </span>
    )
  }
];

const FIAT_OPTIONS = ['USD', 'INR', 'AED', 'EUR', 'GBP', 'CAD', 'AUD', 'SAR', 'QAR'];

const PAYMENT_OPTIONS = [
  'All payment methods',
  'Bank Transfer',
  'UPI',
  'Paytm',
  'PhonePe',
  'Google Pay (GPay)',
  'IMPS',
  'PayPal',
  'Wise',
  'Revolut',
  'Cash Deposit to Bank',
  'Zelle'
];

const P2pFindAdModal = ({ isOpen, onClose, onFind }) => {
  const navigate = useNavigate();

  const [side, setSide] = useState('Buy'); // 'Buy' | 'Sell'
  const [asset, setAsset] = useState('USDT');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [paymentMethod, setPaymentMethod] = useState('All payment methods');

  // Dropdown states
  const [isSideDropdownOpen, setIsSideDropdownOpen] = useState(false);
  const [isAssetDropdownOpen, setIsAssetDropdownOpen] = useState(false);
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
  const [isPaymentDropdownOpen, setIsPaymentDropdownOpen] = useState(false);

  const sideRef = useRef(null);
  const assetRef = useRef(null);
  const currencyRef = useRef(null);
  const paymentRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (sideRef.current && !sideRef.current.contains(e.target)) {
        setIsSideDropdownOpen(false);
      }
      if (assetRef.current && !assetRef.current.contains(e.target)) {
        setIsAssetDropdownOpen(false);
      }
      if (currencyRef.current && !currencyRef.current.contains(e.target)) {
        setIsCurrencyDropdownOpen(false);
      }
      if (paymentRef.current && !paymentRef.current.contains(e.target)) {
        setIsPaymentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') return null;

  const currentCoin = COIN_OPTIONS.find((c) => c.symbol === asset) || COIN_OPTIONS[0];

  const handleFindAds = (e) => {
    if (e) e.preventDefault();
    if (onFind) {
      onFind({ side, asset, amount, currency, paymentMethod });
    }
    onClose();
    // Navigate to P2P dashboard with filters
    const queryParams = new URLSearchParams({
      side: side.toLowerCase(),
      asset: asset,
      currency: currency,
      ...(amount ? { amount } : {}),
      ...(paymentMethod !== 'All payment methods' ? { payment: paymentMethod } : {})
    }).toString();

    navigate(`/p2p-dashboard?${queryParams}`, {
      state: {
        side: side.toLowerCase(),
        asset,
        currency,
        amount,
        payment: paymentMethod
      }
    });
  };

  return createPortal(
    <div
      className="p2p-fam-overlay"
      onClick={onClose}
      style={{ zIndex: 1000000000 }}
    >
      <div
        className="p2p-fam-card"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="p2p-fam-title"
      >
        {/* ================= HEADER ================= */}
        <div className="p2p-fam-header">
          <div className="p2p-fam-header-content">
            <h2 id="p2p-fam-title" className="p2p-fam-title">
              Find an Ad
            </h2>
            <button
              type="button"
              className="p2p-fam-close-btn"
              onClick={onClose}
              aria-label="Close"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <p className="p2p-fam-subtitle">
            Share your trading requirements, and we'll help you filter available
            offers in the AGCX P2P marketplace.
          </p>
        </div>

        {/* ================= FORM BODY ================= */}
        <div className="p2p-fam-form">
          {/* Row 1: I want to */}
          <div className="p2p-fam-field-group">
            <label className="p2p-fam-label">I want to</label>
            <div className="p2p-fam-row-two">
              {/* Buy / Sell dropdown */}
              <div className="p2p-fam-dropdown-wrap" ref={sideRef}>
                <button
                  type="button"
                  className={`p2p-fam-select-box ${isSideDropdownOpen ? 'active' : ''}`}
                  onClick={() => {
                    setIsSideDropdownOpen(!isSideDropdownOpen);
                    setIsAssetDropdownOpen(false);
                    setIsCurrencyDropdownOpen(false);
                    setIsPaymentDropdownOpen(false);
                  }}
                >
                  <span className="p2p-fam-select-val">{side}</span>
                  <svg
                    className={`p2p-fam-chevron ${isSideDropdownOpen ? 'rotate' : ''}`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {isSideDropdownOpen && (
                  <ul className="p2p-fam-dropdown-menu">
                    <li
                      className={`p2p-fam-menu-item ${side === 'Buy' ? 'selected' : ''}`}
                      onClick={() => {
                        setSide('Buy');
                        setIsSideDropdownOpen(false);
                      }}
                    >
                      Buy
                    </li>
                    <li
                      className={`p2p-fam-menu-item ${side === 'Sell' ? 'selected' : ''}`}
                      onClick={() => {
                        setSide('Sell');
                        setIsSideDropdownOpen(false);
                      }}
                    >
                      Sell
                    </li>
                  </ul>
                )}
              </div>

              {/* Crypto Asset Dropdown */}
              <div className="p2p-fam-dropdown-wrap" ref={assetRef}>
                <button
                  type="button"
                  className={`p2p-fam-select-box ${isAssetDropdownOpen ? 'active' : ''}`}
                  onClick={() => {
                    setIsAssetDropdownOpen(!isAssetDropdownOpen);
                    setIsSideDropdownOpen(false);
                    setIsCurrencyDropdownOpen(false);
                    setIsPaymentDropdownOpen(false);
                  }}
                >
                  <div className="p2p-fam-coin-badge">
                    {currentCoin.icon}
                    <span className="p2p-fam-select-val">{currentCoin.symbol}</span>
                  </div>
                  <svg
                    className={`p2p-fam-chevron ${isAssetDropdownOpen ? 'rotate' : ''}`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {isAssetDropdownOpen && (
                  <ul className="p2p-fam-dropdown-menu">
                    {COIN_OPTIONS.map((coin) => (
                      <li
                        key={coin.symbol}
                        className={`p2p-fam-menu-item ${asset === coin.symbol ? 'selected' : ''}`}
                        onClick={() => {
                          setAsset(coin.symbol);
                          setIsAssetDropdownOpen(false);
                        }}
                      >
                        <div className="p2p-fam-coin-badge">
                          {coin.icon}
                          <span>{coin.symbol}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Row 2: Payment currency / amount */}
          <div className="p2p-fam-field-group">
            <label className="p2p-fam-label">Payment currency / amount</label>
            <div className="p2p-fam-amount-box">
              <input
                type="text"
                className="p2p-fam-amount-input"
                placeholder="Enter Amount"
                value={amount}
                onChange={(e) => {
                  const val = e.target.value;
                  // Allow only numbers and optional single decimal point
                  if (/^\d*\.?\d*$/.test(val)) {
                    setAmount(val);
                  }
                }}
              />

              {/* Currency Dropdown Selector */}
              <div className="p2p-fam-currency-wrap" ref={currencyRef}>
                <button
                  type="button"
                  className="p2p-fam-currency-btn"
                  onClick={() => {
                    setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen);
                    setIsSideDropdownOpen(false);
                    setIsAssetDropdownOpen(false);
                    setIsPaymentDropdownOpen(false);
                  }}
                >
                  <span className="p2p-fam-currency-code">{currency}</span>
                  <svg
                    className={`p2p-fam-chevron ${isCurrencyDropdownOpen ? 'rotate' : ''}`}
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {isCurrencyDropdownOpen && (
                  <ul className="p2p-fam-dropdown-menu p2p-fam-currency-menu">
                    {FIAT_OPTIONS.map((fiat) => (
                      <li
                        key={fiat}
                        className={`p2p-fam-menu-item ${currency === fiat ? 'selected' : ''}`}
                        onClick={() => {
                          setCurrency(fiat);
                          setIsCurrencyDropdownOpen(false);
                        }}
                      >
                        {fiat}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Row 3: Payment Methods */}
          <div className="p2p-fam-field-group">
            <label className="p2p-fam-label">Payment Methods</label>
            <div className="p2p-fam-dropdown-wrap full-width" ref={paymentRef}>
              <button
                type="button"
                className={`p2p-fam-select-box ${isPaymentDropdownOpen ? 'active' : ''}`}
                onClick={() => {
                  setIsPaymentDropdownOpen(!isPaymentDropdownOpen);
                  setIsSideDropdownOpen(false);
                  setIsAssetDropdownOpen(false);
                  setIsCurrencyDropdownOpen(false);
                }}
              >
                <span className="p2p-fam-select-val">{paymentMethod}</span>
                <svg
                  className={`p2p-fam-chevron ${isPaymentDropdownOpen ? 'rotate' : ''}`}
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {isPaymentDropdownOpen && (
                <ul className="p2p-fam-dropdown-menu p2p-fam-payment-menu">
                  {PAYMENT_OPTIONS.map((method) => (
                    <li
                      key={method}
                      className={`p2p-fam-menu-item ${paymentMethod === method ? 'selected' : ''}`}
                      onClick={() => {
                        setPaymentMethod(method);
                        setIsPaymentDropdownOpen(false);
                      }}
                    >
                      {method}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* CTA Button: Find Ads Now */}
          <button
            type="button"
            className="p2p-fam-submit-btn"
            onClick={handleFindAds}
          >
            Find Ads Now
          </button>
        </div>

        {/* ================= 3-STEP PROCESS GUIDE ================= */}
        <div className="p2p-fam-steps-container">
          {/* Step 1 */}
          <div className="p2p-fam-step-col">
            <div className="p2p-fam-step-icon-wrap step-1-bg">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#D97706"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <line x1="10" y1="9" x2="8" y2="9" />
              </svg>
              <div className="p2p-fam-badge step-1-badge">
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#D97706"
                  strokeWidth="3.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </div>
            </div>
            <h4 className="p2p-fam-step-title">1. Pick an Ad</h4>
            <p className="p2p-fam-step-desc">
              Choose the best offer that suits you
            </p>
          </div>

          {/* Divider Arrow 1 */}
          <div className="p2p-fam-step-arrow">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#D1D5DB"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>

          {/* Step 2 */}
          <div className="p2p-fam-step-col">
            <div className="p2p-fam-step-icon-wrap step-2-bg">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#10B981"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
              <div className="p2p-fam-badge step-2-badge">
                <svg
                  width="8"
                  height="8"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
            <h4 className="p2p-fam-step-title">2. Make payment</h4>
            <p className="p2p-fam-step-desc">
              Pay securely using your preferred method
            </p>
          </div>

          {/* Divider Arrow 2 */}
          <div className="p2p-fam-step-arrow">
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#D1D5DB"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </div>

          {/* Step 3 */}
          <div className="p2p-fam-step-col">
            <div className="p2p-fam-step-icon-wrap step-3-bg">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#7E22CE"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <div className="p2p-fam-badge step-3-badge">
                <svg
                  width="8"
                  height="8"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
            <h4 className="p2p-fam-step-title">3. Receive Crypto</h4>
            <p className="p2p-fam-step-desc">
              Once confirmed, crypto is released to you
            </p>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default P2pFindAdModal;
